// src/resources/reports/ReportPushNotifications/index.tsx
//
// Drill-down "Notificações push" do Painel 360 — resolve o problema do §1 do
// spec (docs/superpowers/specs/2026-09-17-relatorio-push-notifications-design.md,
// pontuax-backend): "5.763 disparos" não diz nada pro dono do posto, porque a
// maioria é campanha automática (um disparo por cliente). Aqui ele vê taxa de
// entrega/leitura/clique, quantas mensagens foram campanha vs envio único, a
// timeline de quando cada envio saiu e qual mensagem funcionou melhor.
// Estrutura e estilo espelham ReportAppPenetration/ (feature 153, entregue hoje).
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import {
  AutocompleteInput, DateField, Datagrid, Filter,
  ReferenceInput, SelectArrayInput, TextField, useListContext, usePermissions,
} from 'react-admin';
import { Permission, ReportList } from '../../../components';
import { BetweenDatesInput } from '../../../components/BetweenDatesInput';
import RequiredChain from '../../../components/RequiredChain';
import { PERMISSIONS } from '../../../constants';
import { getChainId } from '../../../lib';
import { filterToQuery } from '../../../utils';
import FunnelCards from './FunnelCards';
import HourlyChart from './HourlyChart';
import MarketBenchmark from './MarketBenchmark';
import MessagesTable from './MessagesTable';
import OriginBreakdown from './OriginBreakdown';
import TimelineChart from './TimelineChart';
import {
  DeltaPp, ORIGIN_CHOICES, ORIGIN_LABELS, PushDetailsData, engagementRate, influencedOpenRate, ratePp,
} from './types';
import { pushNotificationsExporter } from './export';

const FiltersReportList = (props: any) => {
  const { permissions } = usePermissions();

  return (
    <Filter {...props}>
      <BetweenDatesInput alwaysOn />
      {permissions && permissions.includes(PERMISSIONS.ADMIN) && (
        <ReferenceInput
          source="chainId"
          reference="chains"
          sort={{ field: 'name', order: 'ASC' }}
          // Mesmo truque do ReportAppPenetration: `null` (não `undefined`)
          // desliga o corte de 25 registros do react-admin, senão uma rede
          // com >25 postos perde opções — aqui não tem posto, mas o filtro
          // de rede é o mesmo componente e sofre do mesmo corte.
          perPage={null as unknown as number}
          alwaysOn
        >
          <AutocompleteInput
            filterToQuery={filterToQuery}
            label="Rede"
            style={{ minWidth: 180 }}
            defaultValue={getChainId()}
            optionText="name"
          />
        </ReferenceInput>
      )}
      {/* Sem filtro de posto: dispatch_message não tem posto — o público de
          uma push é por etiqueta/grupo, não por unidade (§3 do spec). */}
      <SelectArrayInput
        source="origins"
        label="Tipo de envio"
        choices={ORIGIN_CHOICES}
        style={{ minWidth: 220 }}
        alwaysOn
      />
    </Filter>
  );
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  // `origins` é filtro estático (enum, não referência) — junta os rótulos num
  // texto simples em vez de ReferenceArrayField (que espera ids de outro resource).
  const originNames = (filterValues.origins ?? []).map((id: string) => ORIGIN_LABELS[id as keyof typeof ORIGIN_LABELS]).join(', ') || 'Todos';

  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, originNames, generatedReportAt: moment().toISOString() }]} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <TextField source="originNames" textAlign="center" label="Tipos de envio" sortable={false} />
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
};

const PushNotificationsReport = () => {
  const { data } = useListContext();
  const details = data?.[0] as unknown as PushDetailsData | undefined;

  if (!details) return null;

  // Período sem nenhum disparo: um aviso só, em vez de quatro cards zerados, um gráfico vazio e
  // uma tabela vazia — que dariam a impressão de erro na tela.
  if (!details.funnel.dispatches) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography color="text.secondary">Nenhuma notificação enviada no período selecionado.</Typography>
      </Box>
    );
  }

  // O backend ainda não manda deltaPp das duas taxas novas (influencedOpens é campo recente no
  // contrato — ver comentário em types.ts), então completa aqui a partir do funil atual x
  // anterior, com o mesmo arredondamento do `pushDeltaPp` do backend (`ratePp`).
  const deltaPp: DeltaPp = {
    ...details.deltaPp,
    influencedOpenRate: ratePp(influencedOpenRate(details.funnel), influencedOpenRate(details.previous)),
    engagementRate: ratePp(engagementRate(details.funnel), engagementRate(details.previous)),
  };

  return (
    <Box sx={{ p: 2 }}>
      <FunnelCards funnel={details.funnel} deltaPp={deltaPp} />
      <MarketBenchmark readRate={details.funnel.readRate} />
      <OriginBreakdown byOrigin={details.byOrigin} />
      <TimelineChart series={details.series} />
      <HourlyChart hourly={details.hourly} />
      <MessagesTable messages={details.messages} messagesTruncated={details.messagesTruncated} messagesTotal={details.messagesTotal} />

      {/* §3 item 1 do spec — em português de dono de posto: o Android com o app fechado não
          confirma recebimento (o sistema exibe a notificação sem acordar o app), então
          "Confirmadas pelo app" NÃO é taxa de entrega e não deve ser lida como falha de entrega.
          "Lida"/"clicada" seguem exigindo confirmação do app pelo mesmo motivo. */}
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 1 }}>
        "Confirmadas pelo app" não é taxa de entrega: no Android, quando o app está fechado, o aparelho exibe a notificação sozinho e o app nunca fica sabendo — por isso esse número fica baixo mesmo quando a entrega foi normal. "Lida" e "clicada" também só contam quando o aplicativo confirma ao servidor. Já "Aberturas influenciadas" pega quem recebeu a push, não tocou nela, mas abriu o app pelo ícone em até 24h — é sinal real de resultado do envio.
        {details.legacyFallback && ' Período inclui envios antigos calculados de outra forma (antes de jun/2026) — os totais continuam corretos, só a origem do cálculo muda.'}
      </Typography>
    </Box>
  );
};

// props: any — ReportList (componente legado, sem tipos) infere o shape de
// props exigido a partir de UM dos call sites; espalhar `{...props}`, como
// ReportAppPenetration.tsx já faz, evita TS2740 (propriedades "faltando" que
// na real nunca são passadas por ninguém).
const ReportPushNotifications = (props: any) => {
  const filterValues: Record<string, any> = {
    chainId: getChainId(),
    from: moment().startOf('day').subtract(1, 'month').toISOString(),
    to: moment().endOf('day').toISOString(),
  };

  return (
    <Permission permission={PERMISSIONS.DASHBOARD_V2}>
      <RequiredChain>
        <ReportList periodRequired
          {...props}
          title="Desempenho das notificações"
          resource="dashboard-v2/push-details"
          filters={<FiltersReportList />}
          sort={{ field: 'id', order: 'ASC' }}
          filterDefaultValues={filterValues}
          showPagination={false}
          bulkActionButtons={false}
          customExporter={pushNotificationsExporter}
          titleOnPrint="Relatório de Desempenho das Notificações"
          filterTablePrint={<FilterTablePrint />}
          landscape
        >
          <PushNotificationsReport />
        </ReportList>
      </RequiredChain>
    </Permission>
  );
};

export default ReportPushNotifications;
